<script>
  import { ScrollDownMessage } from '@st-graphics/graphics-components'

  //import { md, boolean } from '@st-graphics/graphics-kit/utils'
  import Scroller from '@sveltejs/svelte-scroller'

  import { introJson } from '$state/data'

  import WordsCanvas from './WordsCanvas.svelte'
  import AnimationsConfig from './AnimationsConfig.svelte'

  let {
    value,
    cards = [],
    cardsalign = 'center', // left, center, right

    headercard = undefined, // for header scrolly only
    headlineposition = 'start', // for header scrolly only
    // scrolly settings
    top = 0,
    bottom = 0,
    threshold = 1,
    count = $bindable(0),
    index = $bindable(0),
    offset = $bindable(0),
    progress = $bindable(0),
  } = $props()

  // Animations State
  let debug = false
  let enableTypingEffect = $state(false)
  let enableLastCardTypingEffect = $state(false)
  let enableBorderAnimation = $state(true)
  let rowSpacingDesktop = $state(45)
  let rowSpacingMobile = $state(30)
  let activeDescFontSize = $state(18)
  let activeDescLineHeight = $state(1.5)
  let activeDescGap = $state(25)
  let activeBoxBgColor = $state('rgba(63, 85, 165, 0.35)')
  let animSpeed = $state(0.015)
  let emptyCardFontSizeDesktop = $state(30)
  let emptyCardFontSizeMobile = $state(22)
  let emptyCardLineHeight = $state(1.5)
  let emptyCardBgOpacity = $state(0.25)

  const requiredWords = $derived(
    cards
      .filter((c) => c.word || c.description)
      .map((c) => ({
        word: c.word || '',
        description: c.description || '',
      })),
  )
</script>

{#if debug}
  <AnimationsConfig
    bind:enableTypingEffect
    bind:enableLastCardTypingEffect
    bind:enableBorderAnimation
    bind:rowSpacingDesktop
    bind:rowSpacingMobile
    bind:activeDescFontSize
    bind:activeDescLineHeight
    bind:activeDescGap
    bind:activeBoxBgColor
    bind:animSpeed
    bind:emptyCardFontSizeDesktop
    bind:emptyCardFontSizeMobile
    bind:emptyCardLineHeight
    bind:emptyCardBgOpacity
  />
{/if}

<Scroller
  {top}
  {bottom}
  {threshold}
  bind:count
  bind:index
  bind:offset
  bind:progress
  query=".scrolly-card"
>
  {#snippet background()}
    <div class="scrolly-background">
      <div class="sr-only" aria-live="assertive">
        Scrolly background is updated for slide {index + 1}. {cards[index]?.bgalt || ''}
      </div>

      {#if debug}
        <div
          class="debug"
          style="position:absolute; top:0; left:0; z-index:10; font-size: 14px; z-index: 999;"
        >
          card:{index} | {offset.toFixed(2)}<br />
        </div>
      {/if}

      <div>
        <WordsCanvas
          {index}
          {offset}
          {progress}
          data={introJson.data}
          {enableTypingEffect}
          {enableLastCardTypingEffect}
          {enableBorderAnimation}
          {requiredWords}
          {rowSpacingDesktop}
          {rowSpacingMobile}
          {activeDescFontSize}
          {activeDescLineHeight}
          {activeDescGap}
          {activeBoxBgColor}
          {animSpeed}
          {emptyCardFontSizeDesktop}
          {emptyCardFontSizeMobile}
          {emptyCardLineHeight}
          {emptyCardBgOpacity}
        />
      </div>

      <div class="sdm" class:hidden={index > 0 || offset > 0.05}>
        <ScrollDownMessage />
      </div>
    </div>
  {/snippet}

  {#snippet foreground()}
    <div class="scrolly-foreground">
      <div class="cards-container {cardsalign}">
        {#each cards as { text, thumbnail, alt, classname = undefined, lazy = false }, i (i)}
          <div
            class="scrolly-section scrolly-card {classname} blank"
            style={i < 4 ? 'border: none; background: transparent;' : ''}
          >
            <!--<p class="scrolly-text">{text}</p>-->
          </div>
        {/each}
      </div>

      <div class="header-card {headlineposition}">
        {@render headercard()}
      </div>
    </div>
  {/snippet}
</Scroller>

<style lang="scss">
  .sdm {
    position: absolute;
    bottom: 100px;
    z-index: 9999;
    width: 100%;
    text-align: center;
  }

  .hide {
    display: none;
  }
  .scrolly-background {
    position: relative;
    height: 100vh;
    min-height: 100vh;
    contain: layout paint;
    overflow: hidden;
    will-change: transform;
  }
  .scrolly-background.no-overflow {
    overflow: visible;
  }
  .scrolly-foreground {
    display: flex;
    flex-direction: column;
    align-items: var(--scrolly-foreground-align-items, center);
    padding: 0 1rem;
    margin: 0 auto;
    transform: translateZ(0);
  }
  .scrolly-foreground .header-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 40vh;
    margin-top: -35vh; // adjust last card duration here
  }
  .scrolly-foreground .header-card.start {
    margin-top: -100vh;
  }
  .scrolly-foreground .cards-container {
    display: flex;
    flex-direction: column;
    align-items: var(--md-scrolly-foreground-align-items, start);
    width: 100%;
    max-width: 75rem;
    padding-bottom: max(50rem, 100vh);
    margin: 0 auto;
  }
  .scrolly-foreground .cards-container.left {
    align-items: flex-start;
  }
  .scrolly-foreground .cards-container.center {
    align-items: center;
  }
  .scrolly-foreground .cards-container.right {
    align-items: flex-end;
  }
  .scrolly-foreground .cards-container .scrolly-section {
    margin-bottom: max(50rem, 100vh);
  }
  .scrolly-foreground .cards-container .scrolly-card {
    width: 100%;
    max-width: var(--scrolly-card-max-width, 25rem);
    line-height: var(--scrolly-card-line-height, 1.9375rem);
    text-align: var(--scrolly-card-text-align, unset);
  }
  .scrolly-foreground .cards-container .scrolly-card .scrolly-text {
    padding: var(--scrolly-text-padding, 0.625rem 0.9375rem 1.25rem);
  }
  .scrolly-foreground .cards-container .scrolly-card .scrolly-text:only-child {
    padding-top: 0.9375rem;
  }
  .scrolly-foreground .cards-container .scrolly-card:last-child {
    margin-bottom: 0;
  }
  @media (width >= 744px) {
    .scrolly-foreground .cards-container .scrolly-card {
      margin: 0 0.625rem max(50rem, 100vh);
    }
  }

  .scrolly-background {
    background: var(
      --scrolly-background-background,
      var(--theme-scrolly-background-background, transparent)
    );
  }
  .scrolly-foreground .cards-container .scrolly-card {
    font-size: var(--scrolly-card-font-size, 1.25rem);
    color: var(--scrolly-card-color, var(--theme-scrolly-card-color, var(--st-black)));
    background: var(
      --scrolly-card-background,
      var(--theme-scrolly-card-background, var(--st-white))
    );
    //border: 1px solid var(--project-highlight-color);
  }
  @media (width >= 744px) {
    .scrolly-foreground .cards-container .scrolly-card {
      font-size: 1.375rem;
    }
  }
  .scrolly-foreground .cards-container .scrolly-card.blank {
    opacity: 0;
  }

  :global(svelte-scroller-background-container) {
    position: sticky !important;
    transform: translate(0, 0) !important;
  }

  :global([data-theme='dark']) .scrolly-card {
    --theme-scrolly-card-color: var(--st-white-primary);
    --theme-scrolly-card-background: rgba(12, 43, 87, 0.25);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }

  .header-card {
    position: relative;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: -300px;
      left: 50%;
      transform: translateX(-50%);
      width: 100vw;
      height: calc(100% + 300px);
      background: linear-gradient(0deg, rgba(49, 72, 156, 0.89) 40%, rgba(104, 91, 225, 0) 100%);
      z-index: -1;
      pointer-events: none;
    }
  }

  .sdm {
    position: absolute;
    bottom: 60px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    background: radial-gradient(
      closest-side,
      rgba(49, 73, 156, 0.9) 0%,
      rgba(49, 73, 156, 0.5) 40%,
      rgba(49, 73, 156, 0) 100%
    );
    padding: 40px 20px;
    transition: opacity 0.3s ease;
  }

  .sdm.hidden {
    opacity: 0;
    pointer-events: none;
  }
</style>
